import { useEffect, useId, useRef, useState, type CSSProperties, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { actionProps } from "@/lib/controlAnalytics";
import { formatCount } from "@/lib/format";
import {
    JOBS_STATUS_HINT,
    JOBS_STATUS_TAB,
    JOBS_STATUSES,
    statusTabsLabel,
    type JobsStatus,
} from "@/lib/jobs";

const LAST_STATUS = JOBS_STATUSES.length - 1;
/** How long the pointer or keyboard rests on a tab before its tooltip shows. */
const HINT_DELAY_MS = 1500;
const HINT_MAX_WIDTH = 260;
/** Room below a tab for the longest sentence, or the tooltip opens above it. */
const HINT_ROOM = 72;
const HINT_GAP = 6;
const WINDOW_GUTTER = 12;

/** Which tab a key moves to from `current`, or null for a key that moves nothing. */
function tabAfter(key: string, current: number): number | null {
    if (key === "ArrowRight") return current === LAST_STATUS ? 0 : current + 1;
    if (key === "ArrowLeft") return current === 0 ? LAST_STATUS : current - 1;
    if (key === "Home") return 0;
    if (key === "End") return LAST_STATUS;
    return null;
}

/** Under the tab, or over it near the bottom of the window, and inside the window. */
function hintPosition(tab: DOMRect): CSSProperties {
    const maxWidth = Math.min(HINT_MAX_WIDTH, window.innerWidth - 2 * WINDOW_GUTTER);
    const left = Math.max(
        WINDOW_GUTTER,
        Math.min(tab.left, window.innerWidth - maxWidth - WINDOW_GUTTER),
    );
    const upward = tab.bottom + HINT_ROOM > window.innerHeight;
    return {
        position: "fixed",
        left,
        maxWidth,
        ...(upward
            ? { bottom: window.innerHeight - tab.top + HINT_GAP }
            : { top: tab.bottom + HINT_GAP }),
    };
}

/**
 * Where the person's application stands, as tabs over the results. In
 * queue and Applied count all of the person's where envoy lists them, and
 * Untracked counts this page; on one company's page every tab counts the
 * page. The tablist's label says which. The
 * tabs scroll sideways when they cannot all fit, and arrow keys move between
 * them. A tab rested on says what it holds; the tooltip sits on the body, since
 * the scrolling strip would clip it.
 */
export default function JobsStatusTabs({
    status,
    counts,
    envoyCounts = false,
    onStatus,
}: {
    status: JobsStatus;
    /** Each tab's count, where it has one. Undefined until any is known. */
    counts: Partial<Record<JobsStatus, number>> | undefined;
    /** In queue and Applied count envoy's totals, not this page. */
    envoyCounts?: boolean;
    onStatus: (status: JobsStatus) => void;
}) {
    const [hint, setHint] = useState<{ option: JobsStatus; position: CSSProperties } | null>(null);
    const waiting = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const hintId = useId();
    const hideHint = () => {
        clearTimeout(waiting.current);
        setHint(null);
    };
    const hintLater = (option: JobsStatus, tab: HTMLElement) => {
        hideHint();
        waiting.current = setTimeout(
            () => setHint({ option, position: hintPosition(tab.getBoundingClientRect()) }),
            HINT_DELAY_MS,
        );
    };
    useEffect(() => () => clearTimeout(waiting.current), []);
    useEffect(() => {
        if (!hint) return;
        const escape = (event: globalThis.KeyboardEvent) => {
            if (event.key === "Escape") setHint(null);
        };
        const moved = () => setHint(null);
        document.addEventListener("keydown", escape);
        window.addEventListener("resize", moved);
        window.addEventListener("scroll", moved, true);
        return () => {
            document.removeEventListener("keydown", escape);
            window.removeEventListener("resize", moved);
            window.removeEventListener("scroll", moved, true);
        };
    }, [hint]);
    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        const next = tabAfter(event.key, JOBS_STATUSES.indexOf(status));
        const option = next === null ? undefined : JOBS_STATUSES[next];
        if (option === undefined) return;
        event.preventDefault();
        onStatus(option);
        event.currentTarget.querySelector<HTMLElement>(`[data-status="${option}"]`)?.focus();
    };
    return (
        <div className="min-w-0 border-b border-line">
            <div
                role="tablist"
                aria-label={statusTabsLabel(envoyCounts)}
                onKeyDown={onKeyDown}
                className="-mb-px flex min-w-0 gap-1 overflow-x-auto"
            >
                {JOBS_STATUSES.map((option) => {
                    const selected = status === option;
                    const count = option === "all" ? undefined : counts?.[option];
                    return (
                        <button
                            {...actionProps("job_status_selected", { source: "Jobs", option })}
                            key={option}
                            data-status={option}
                            type="button"
                            role="tab"
                            aria-selected={selected}
                            aria-describedby={hint?.option === option ? hintId : undefined}
                            tabIndex={selected ? 0 : -1}
                            onClick={() => {
                                hideHint();
                                onStatus(option);
                            }}
                            onMouseEnter={(event) => hintLater(option, event.currentTarget)}
                            onMouseLeave={hideHint}
                            onFocus={(event) => {
                                if (event.currentTarget.matches(":focus-visible"))
                                    hintLater(option, event.currentTarget);
                            }}
                            onBlur={hideHint}
                            className={`inline-flex shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2.5 text-[13px] focus-visible:ring-focus max-[760px]:min-h-11 ${
                                selected
                                    ? "border-brand-ink font-semibold text-brand-ink"
                                    : "border-transparent text-fg-muted hover:text-fg"
                            }`}
                        >
                            {JOBS_STATUS_TAB[option]}
                            {count !== undefined && (
                                <span className="rounded-md bg-card-raised px-1.5 py-px font-mono text-[11px] font-normal text-fg-muted">
                                    {formatCount(count)}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
            {hint &&
                createPortal(
                    <div
                        id={hintId}
                        role="tooltip"
                        style={hint.position}
                        className="pointer-events-none z-[70] rounded-lg border border-line bg-card px-3 py-2 text-[12px] leading-snug text-fg shadow-pop"
                    >
                        {JOBS_STATUS_HINT[hint.option]}
                    </div>,
                    document.body,
                )}
        </div>
    );
}
